import { createFileRoute } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { motion } from 'framer-motion';
import { CheckCircle, Phone, Clock, MapPin } from 'lucide-react';
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { trackWhatsAppClick } from "@/lib/gtm";

export const Route = createFileRoute("/orcamento")({
  component: QuotePage,
});

function QuotePage() {
  const steps = [
    { title: 'Análise Técnica', desc: 'Entendemos o seu ambiente e o nível de ruído.' },
    { title: 'Medição Precisa', desc: 'Coletamos as medidas exatas para um projeto sob medida.' },
    { title: 'Orçamento Detalhado', desc: 'Apresentamos a melhor solução técnica e estética.' }
  ];

  return (
    <>
      <SEO 
        title="Orçamento de janelas acústicas no Rio de Janeiro | STOP SOM"
        description="Solicite orçamento para janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro."
      />
      <div className="bg-white">
      {/* Hero Interno */}
      <section className="pt-40 pb-20 bg-secondary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10" />
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold mb-6">Solicite seu orçamento de janelas acústicas no Rio de Janeiro</h1>
            <p className="text-2xl opacity-90 leading-relaxed max-w-2xl mx-auto">
              Conte para nossa equipe o que você precisa e receba orientação para escolher a melhor solução em janelas e esquadrias acústicas sob medida. Atendemos todo o Rio de Janeiro com fábrica em Duque de Caxias - RJ.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-24 px-6">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            {/* Informações Auxiliares */}
            <div className="lg:col-span-4 space-y-12 text-center lg:text-left">
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-8 uppercase tracking-wider">Como funciona nosso atendimento?</h2>
                <div className="space-y-10">
                  {steps.map((step, i) => (
                    <div key={i} className="flex flex-col items-center lg:items-start lg:flex-row gap-4 group">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0 group-hover:bg-primary group-hover:text-white transition-all">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                        <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 p-10 rounded-[3rem] border border-gray-100">
                <h3 className="font-bold text-primary mb-6 flex items-center gap-2">
                  <CheckCircle className="text-secondary" /> Benefícios da STOP SOM
                </h3>
                <ul className="space-y-4">
                  {[
                    "Alta performance acústica",
                    "Instalação limpa e rápida",
                    "Sem quebra de paredes",
                    "Projetos 100% personalizados"
                  ].map((benefit, i) => (
                    <li key={i} className="text-gray-600 text-sm flex items-center gap-3">
                      <div className="w-1.5 h-1.5 bg-secondary rounded-full" /> {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col gap-4">
                <p className="font-bold text-primary text-center">Preferência por atendimento rápido?</p>
                <a 
                  href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                  onClick={() => trackWhatsAppClick("Orçamento via WhatsApp", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "orcamento")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="orcamento"
                  className="bg-accent text-primary py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-secondary transition-all"
                >
                  <WhatsAppIcon className="w-6 h-6" /> Orçamento via WhatsApp
                </a>
              </div>
            </div>

            {/* Formulário de Orçamento */}
            <div className="lg:col-span-8 bg-white p-8 lg:p-16 rounded-[3rem] shadow-2xl border border-gray-50">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-10">Preencha o formulário abaixo</h2>
              <form className="space-y-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">Nome Completo</label>
                    <input type="text" className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" placeholder="João Silva" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">WhatsApp</label>
                    <input type="tel" className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" placeholder="(21) 99269-8354" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">E-mail</label>
                    <input type="email" className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" placeholder="joao@email.com" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">Cidade/Bairro</label>
                    <input type="text" className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" placeholder="Ex: Rio de Janeiro, Barra" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">Ambiente</label>
                    <select className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium appearance-none">
                      <option>Selecione o ambiente</option>
                      <option>Quarto</option>
                      <option>Sala</option>
                      <option>Escritório / Home Office</option>
                      <option>Consultório</option>
                      <option>Comercial</option>
                      <option>Outros</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">Tipo de Ruído</label>
                    <select className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium appearance-none">
                      <option>Qual ruído te incomoda?</option>
                      <option>Trânsito (Carros/Motos)</option>
                      <option>Obras</option>
                      <option>Comércio / Pessoas</option>
                      <option>Latidos / Animais</option>
                      <option>Outros</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-2">Produto de Interesse</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                    {["Janela de Correr", "Persiana Integrada", "Maxim-Ar", "Janela de Giro"].map((prod) => (
                      <label key={prod} className="cursor-pointer group">
                        <input type="checkbox" className="hidden peer" />
                        <div className="p-4 rounded-xl border-2 border-gray-100 peer-checked:border-secondary peer-checked:bg-secondary/5 text-center transition-all">
                          <span className="text-xs font-bold text-gray-500 peer-checked:text-secondary group-hover:text-primary">{prod}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-2">Mensagem (Opcional)</label>
                  <textarea className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium min-h-[120px]" placeholder="Conte-nos mais sobre seu projeto..." />
                </div>

                <button 
                  type="submit" 
                  onClick={() => trackWhatsAppClick("Enviar Solicitação de Orçamento", "/orcamento", "orcamento-form")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="orcamento-form"
                  className="w-full bg-primary text-white py-6 rounded-2xl font-bold text-xl hover:bg-secondary transition-all shadow-2xl shadow-primary/20 transform hover:-translate-y-1"
                >
                  Enviar Solicitação de Orçamento
                </button>
                <p className="text-center text-xs text-gray-400 mt-6">Ao enviar, você concorda com nossa Política de Privacidade.</p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contato Final */}
      <section className="py-10 md:py-20 bg-gray-50 px-6">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-center items-center gap-12">
            <div className="flex items-center gap-4">
              <Phone className="text-secondary shrink-0" />
              <a href="tel:+5521992698354" className="font-bold text-primary hover:text-secondary transition-colors">(21) 99269-8354</a>
            </div>
            <div className="flex items-center gap-4 text-center md:text-left">
              <MapPin className="text-secondary shrink-0" />
              <span className="font-bold text-primary">Fábrica: Rua Itatinga, 511 — Duque de Caxias - RJ</span>
            </div>
            <div className="flex items-center gap-4">
              <Clock className="text-secondary shrink-0" />
              <span className="font-bold text-primary">Seg a Sex, 09h às 18h</span>
            </div>
          </div>
        </div>
      </section>

      {/* JSON-LD LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "STOP SOM Janelas Acústicas",
          "image": "https://stopsomesquadrias.com.br/logo.png",
          "@id": "https://stopsomesquadrias.com.br",
          "url": "https://stopsomesquadrias.com.br",
          "telephone": "+55 21 99269-8354",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Rua Itatinga, 511",
            "addressLocality": "Duque de Caxias",
            "addressRegion": "RJ",
            "postalCode": "25000-000",
            "addressCountry": "BR"
          },
          "areaServed": {
            "@type": "State",
            "name": "Rio de Janeiro"
          },
          "description": "Empresa especializada em janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro, com fábrica em Duque de Caxias - RJ."
        })}
      </script>
    </div>
    </>
  );
}
